import { useEffect, useRef } from 'react';
import { useAudioPlayer } from 'react-use-audio-player';
// Cash-register cue for AUTOMATION execution events only. The clip is
// intentionally short (~1 s ka-ching) so the burst-coalescing loop
// sounds continuous when fills stack — see TAIL_MS /
// MAX_EXECUTION_PLAY_MS below for the timing knobs that match the
// asset duration. Manual order fills get a different cue (see
// `manualSound`) so the operator can hear at a glance whether the
// fill they just heard came from automation or from their own click.
import cashRegisterSound from 'src/Sounds/cash-register.mp3';
// Distinct alert tone for errors / manual-intervention events.
// Re-uses the existing doorbell clip — was the previous full-fill
// sound, freed up when fills migrated to the cash register loop.
import alarmSound from 'src/Sounds/electronic-doorbell.mp3';
// Achievement-style cue for MANUAL order fills (a single contract
// the operator placed via the Kalshi/Polymarket UI). One-shot, no
// loop — manual fills don't burst the way auto-unwind fills do.
import manualSound from 'src/Sounds/achive-sound.mp3';
import type {
  NotificationEvent,
  NotificationEventType,
} from '../types/notifications.types';

/**
 * Three distinct audio tiers for the notifications page:
 *
 *   1. **Execution (automation)** — fills, partial fills, cycle
 *      complete from the auto-unwind engine. Plays a cash-register
 *      loop with burst coalescing (one continuous sound per burst,
 *      capped at MAX_EXECUTION_PLAY_MS).
 *
 *   2. **Manual** — a fill on an order the operator placed by hand
 *      via the Kalshi/Polymarket UI. Plays an achievement-style
 *      ding ONCE per event. No loop, no burst coalescing — manual
 *      fills are by definition operator-initiated and rare.
 *
 *   3. **Alarm** — errors and manual-intervention events. Plays a
 *      distinct doorbell tone, throttled by ALARM_COOLDOWN_MS so a
 *      flurry of related errors doesn't blast the operator.
 *
 * Priority order (top wins): **alarm > manual > execution**. An alarm
 * interrupts whatever is playing. A manual cue interrupts the
 * cash-register loop (the next automation fill will restart it).
 * An execution event arriving during an alarm or manual cue is
 * silent — those tiers complete first.
 *
 * Anything not in any set stays silent — the row, color, and Slack
 * post are still there, just no sound. Avoids alarm fatigue on
 * lifecycle / digest / recovery-success events.
 */
const EXECUTION_EVENT_TYPES = new Set<NotificationEventType>([
  // Full fills (automation)
  'maker_filled',
  'recovery_filled',
  'crossing_executed',
  // Partial fills (automation)
  'maker_partial_fill',
  'partial_fill_accumulated',
  'crossing_partial',
  'crossing_accumulated',
  // Cycle done
  'execution_complete',
]);

/**
 * Manual-tier events — a fill on an order the operator placed by
 * hand. We only ring on `manual_order_executed`; `manual_order_placed`
 * stays silent (the click already gave the operator confirmation —
 * a sound would be redundant noise).
 */
const MANUAL_EVENT_TYPES = new Set<NotificationEventType>([
  'manual_order_executed',
]);

/**
 * Events that demand operator attention — every one of these is
 * either a leg-risk situation, a platform that just got disabled,
 * or a system halt the operator must clear by hand.
 *
 * `recovery_filled` is special-cased below: it's an execution event
 * when the counter is `executing` (recovery in progress, no
 * intervention needed) but an alarm when `failed` or `no_bid`
 * (operator must hedge manually).
 */
const ALARM_EVENT_TYPES = new Set<NotificationEventType>([
  'error',
  'counter_failed',
  'out_of_balance',
  'emergency_stop',
  'platform_disconnect',
]);

/** Burst tail for the execution loop. */
const TAIL_MS = 1500;

/** Hard cap on the execution loop per burst. */
const MAX_EXECUTION_PLAY_MS = 5000;

/**
 * Minimum gap between alarm sounds. Several errors firing within
 * this window play ONE alarm — the page row + Slack post still
 * surface every event, but the audio cue isn't repeated. Tuned
 * short enough that a genuine new error 5 s later still alerts.
 */
const ALARM_COOLDOWN_MS = 3000;

/** How long an alarm sound plays before auto-stopping. */
const ALARM_PLAY_MS = 3000;

/**
 * How long the manual-order achievement ding plays before
 * auto-stopping. The asset itself is ~1.5 s; we cap at 2 s so a
 * stale stop-timer can't keep the audio handle busy and block
 * future cues.
 */
const MANUAL_PLAY_MS = 2000;

type Tier = 'execution' | 'manual' | 'alarm';

function classifyEvent(event: NotificationEvent): Tier | null {
  if (ALARM_EVENT_TYPES.has(event.type)) return 'alarm';
  if (event.type === 'recovery_filled') {
    // Recovery "executing" = automation is hedging the leg, no
    // operator action needed → execution tier (cash register).
    // Recovery "failed" / "no_bid" = manual intervention → alarm.
    const counterAction = (event.payload as any)?.counterAction;
    return counterAction === 'executing' ? 'execution' : 'alarm';
  }
  if (MANUAL_EVENT_TYPES.has(event.type)) return 'manual';
  if (EXECUTION_EVENT_TYPES.has(event.type)) return 'execution';
  return null;
}

/**
 * Plays a cash-register cue for execution events and a doorbell
 * alarm for errors / manual-intervention events. Designed around
 * the operator's reality: a quiet automation produces few sounds,
 * a busy unwind produces ONE rolling cash-register loop, and any
 * error that genuinely needs eyes punches through with a distinct
 * alarm.
 *
 * Pass `null`/`undefined` for ticks where the event has already
 * been consumed (`useNotificationsSocket.acknowledge()`). The hook
 * also guards on `event.id` so an unrelated re-render doesn't
 * replay the cue.
 */
export function useNotificationSound(
  event: NotificationEvent | null | undefined,
): void {
  const { load, stop } = useAudioPlayer();
  const lastPlayedId = useRef<string | null>(null);

  // Currently-active tier so an execution loop doesn't clobber an
  // in-progress alarm and a stale event re-render doesn't restart
  // an unrelated sound.
  const currentTier = useRef<Tier | null>(null);

  // Execution-loop bookkeeping (burst-coalesced).
  const executionStartedAt = useRef<number | null>(null);

  // Alarm cooldown — last time an alarm sound started.
  const lastAlarmAt = useRef(0);

  // Single timer ref shared between tiers since only one sound
  // plays at a time. Cleared on every tier transition.
  const stopTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!event) return;
    if (event.id === lastPlayedId.current) return;
    const tier = classifyEvent(event);
    if (!tier) return;

    lastPlayedId.current = event.id;
    const now = Date.now();

    if (tier === 'alarm') {
      // Alarm cooldown — silent if another alarm just fired.
      if (now - lastAlarmAt.current < ALARM_COOLDOWN_MS) return;
      lastAlarmAt.current = now;

      // Interrupt any execution loop / manual ding in progress —
      // alarms have priority. Operator should hear the doorbell
      // over a running cash register or achievement chime.
      if (stopTimer.current) clearTimeout(stopTimer.current);
      executionStartedAt.current = null;
      currentTier.current = 'alarm';
      try {
        load(alarmSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        currentTier.current = null;
        stopTimer.current = null;
      }, ALARM_PLAY_MS);
      return;
    }

    if (tier === 'manual') {
      // Manual order fill — distinct chime so the operator can hear
      // that the click they made just landed (vs the cash-register
      // burst from auto-unwind). Defers to alarms but interrupts an
      // in-progress cash-register loop; the next automation fill
      // will restart the loop naturally.
      if (currentTier.current === 'alarm') return;
      if (stopTimer.current) clearTimeout(stopTimer.current);
      executionStartedAt.current = null;
      currentTier.current = 'manual';
      try {
        load(manualSound, { autoplay: true, loop: false });
      } catch {
        currentTier.current = null;
        return;
      }
      stopTimer.current = setTimeout(() => {
        stop();
        currentTier.current = null;
        stopTimer.current = null;
      }, MANUAL_PLAY_MS);
      return;
    }

    // tier === 'execution' — but if an alarm or manual chime is
    // currently playing, don't interrupt it; those tiers complete
    // naturally and the next execution event restarts the loop.
    if (currentTier.current === 'alarm' || currentTier.current === 'manual')
      return;

    if (executionStartedAt.current === null) {
      executionStartedAt.current = now;
      currentTier.current = 'execution';
      try {
        load(cashRegisterSound, { autoplay: true, loop: true });
      } catch {
        executionStartedAt.current = null;
        currentTier.current = null;
        return;
      }
    }

    const cap = executionStartedAt.current + MAX_EXECUTION_PLAY_MS;
    const stopAt = Math.min(now + TAIL_MS, cap);
    const delay = Math.max(0, stopAt - now);

    if (stopTimer.current) clearTimeout(stopTimer.current);
    stopTimer.current = setTimeout(() => {
      stop();
      executionStartedAt.current = null;
      currentTier.current = null;
      stopTimer.current = null;
    }, delay);
  }, [event, load, stop]);

  // Cleanup on unmount.
  useEffect(() => {
    return () => {
      if (stopTimer.current) clearTimeout(stopTimer.current);
      stop();
    };
  }, [stop]);
}
